import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, useReducedMotion } from 'motion/react';
import { duration } from './primitives/tokens';
import { useControllable } from '../../hooks/useControllable';
import { Icon } from './Icon';
import { staggerItem } from './primitives/motion/presets';
import styles from './Menu.module.css';

// ── CVA ──────────────────────────────────────────────────────────────────────

const menuNav = cva(styles.root, {
  variants: {
    mode: {
      vertical: undefined,
      horizontal: styles.horizontal,
      inline: undefined,
    },
    collapsed: { true: styles.collapsed },
  },
  defaultVariants: { mode: 'vertical' },
});

const menuItemBtn = cva(styles.itemBtn, {
  variants: {
    active: { true: styles.itemBtnActive },
    disabled: { true: styles.itemBtnDisabled },
    collapsed: { true: styles.itemBtnCollapsed },
  },
});

// ── Types ─────────────────────────────────────────────────────────────────────

type MenuMode = 'vertical' | 'horizontal' | 'inline';

interface MenuItemDef {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  type?: 'item' | 'group' | 'divider';
  children?: MenuItemDef[];
}

interface MenuProps extends Omit<React.HTMLAttributes<HTMLElement>, 'onClick' | 'onSelect'> {
  /** Accessible label for the nav element */
  ariaLabel?: string;
  /** Menu items */
  items: MenuItemDef[];
  /** Display mode */
  mode?: MenuMode;
  /** Controlled selected keys */
  selectedKeys?: string[];
  /** Default selected keys */
  defaultSelectedKeys?: string[];
  /** Controlled open sub-menu keys */
  openKeys?: string[];
  /** Default open keys */
  defaultOpenKeys?: string[];
  /** Click handler */
  onSelect?: (key: string) => void;
  /** Collapsed state (inline mode) */
  inlineCollapsed?: boolean;
  /** Override styles applied to the active/selected item button */
  activeItemStyle?: React.CSSProperties;
}

/**
 * Menu — navigation menu with nesting support.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | items | MenuItemDef[] | — | Menu structure |
 * | mode | 'vertical' \| 'horizontal' \| 'inline' | 'vertical' | Display mode |
 * | selectedKeys | string[] | — | Controlled selection |
 * | defaultSelectedKeys | string[] | [] | Default selection |
 * | openKeys | string[] | — | Controlled open sub-menus |
 * | defaultOpenKeys | string[] | [] | Default open sub-menus |
 * | onSelect | (key) => void | — | Selection handler |
 * | inlineCollapsed | boolean | false | Collapse inline menu |
 *
 * @example
 * <Menu items={[
 *   { key: 'home', label: 'Home', icon: <Home size={16} /> },
 *   { key: 'settings', label: 'Settings', children: [
 *     { key: 'profile', label: 'Profile' },
 *     { key: 'account', label: 'Account' },
 *   ]},
 * ]} />
 */
export function Menu({
  items,
  mode = 'vertical',
  selectedKeys: controlledSelected,
  defaultSelectedKeys = [],
  openKeys: controlledOpen,
  defaultOpenKeys = [],
  onSelect,
  inlineCollapsed = false,
  ariaLabel = 'Navigation',
  activeItemStyle,
  style,
  className,
  ...props
}: MenuProps) {
  const [selected, setSelected] = useControllable(controlledSelected, defaultSelectedKeys);
  const [openSubs, setOpenSubs] = useControllable(controlledOpen, defaultOpenKeys);
  const shouldReduce = useReducedMotion();

  const handleSelect = (key: string) => {
    setSelected([key]);
    onSelect?.(key);
  };

  const toggleSub = (key: string) => {
    setOpenSubs(openSubs.includes(key) ? openSubs.filter((k) => k !== key) : [...openSubs, key]);
  };

  const isHorizontal = mode === 'horizontal';

  return (
    <nav
      aria-label={ariaLabel}
      className={menuNav({ mode, collapsed: inlineCollapsed && !isHorizontal, className })}
      style={style}
      {...props}
    >
      {items.map((item, i) => {
        const node = (
          <MenuItem
            item={item}
            selected={selected}
            openSubs={openSubs}
            onSelect={handleSelect}
            onToggleSub={toggleSub}
            mode={mode}
            collapsed={inlineCollapsed}
            depth={0}
            activeItemStyle={activeItemStyle}
          />
        );
        // Skip motion wrapper entirely when reduced-motion is requested so the
        // rendered DOM has no extra wrapper layer for assistive tech.
        if (shouldReduce) return <React.Fragment key={item.key}>{node}</React.Fragment>;
        return (
          <motion.div key={item.key} {...staggerItem(i)}>
            {node}
          </motion.div>
        );
      })}
    </nav>
  );
}

function MenuItem({
  item,
  selected,
  openSubs,
  onSelect,
  onToggleSub,
  mode,
  collapsed,
  depth,
  activeItemStyle,
}: {
  item: MenuItemDef;
  selected: string[];
  openSubs: string[];
  onSelect: (key: string) => void;
  onToggleSub: (key: string) => void;
  mode: MenuMode;
  collapsed: boolean;
  depth: number;
  activeItemStyle?: React.CSSProperties;
}) {
  const isHorizontal = mode === 'horizontal';

  if (item.type === 'divider') {
    return (
      <div
        className={clsx(
          styles.divider,
          isHorizontal ? styles.dividerHorizontal : styles.dividerVertical,
        )}
      />
    );
  }

  if (item.type === 'group') {
    return (
      <div role="group" aria-label={typeof item.label === 'string' ? item.label : undefined}>
        <div className={styles.groupLabel}>
          {!collapsed && item.label}
        </div>
        {item.children?.map((child) => (
          <MenuItem
            key={child.key}
            item={child}
            selected={selected}
            openSubs={openSubs}
            onSelect={onSelect}
            onToggleSub={onToggleSub}
            mode={mode}
            collapsed={collapsed}
            depth={depth + 1}
            activeItemStyle={activeItemStyle}
          />
        ))}
      </div>
    );
  }

  const isActive = selected.includes(item.key);
  const hasSub = item.children && item.children.length > 0;
  const isSubOpen = openSubs.includes(item.key);

  // paddingLeft with depth indentation is a runtime computed value — CSS custom property
  const paddingLeftValue = collapsed ? undefined : `calc(var(--space-3) + ${depth * 16}px)`;

  return (
    <div>
      <motion.button
        onClick={() => (hasSub ? onToggleSub(item.key) : onSelect(item.key))}
        disabled={item.disabled}
        whileTap={item.disabled ? undefined : { scale: 0.98 }}
        transition={{ duration: duration.normal / 1000 }}
        aria-expanded={hasSub ? isSubOpen : undefined}
        aria-haspopup={hasSub ? 'true' : undefined}
        aria-current={isActive && !hasSub ? 'page' : undefined}
        className={menuItemBtn({ active: isActive, disabled: item.disabled, collapsed })}
        // activeItemStyle is an explicit consumer override contract — arbitrary CSS properties
        // must be passed inline. paddingLeft depth-indent is a runtime CSS custom property.
        style={{
          ...(isActive && activeItemStyle),
          ...(paddingLeftValue && { '--item-padding-left': paddingLeftValue } as React.CSSProperties),
        }}
      >
        {item.icon && <span className={styles.itemIcon}>{item.icon}</span>}
        {!collapsed && (
          <span className={styles.itemLabel}>
            {item.label}
          </span>
        )}
        {hasSub && !collapsed && (
          <motion.span
            animate={{ rotate: isSubOpen ? 180 : 0 }}
            transition={{ duration: duration.slow / 1000 }}
            className={styles.chevron}
          >
            <Icon name="expand_more" size={14} />
          </motion.span>
        )}
      </motion.button>
      {hasSub && isSubOpen && !collapsed && (
        <div>
          {item.children!.map((child) => (
            <MenuItem
              key={child.key}
              item={child}
              selected={selected}
              openSubs={openSubs}
              onSelect={onSelect}
              onToggleSub={onToggleSub}
              mode={mode}
              collapsed={collapsed}
              depth={depth + 1}
              activeItemStyle={activeItemStyle}
            />
          ))}
        </div>
      )}
    </div>
  );
}
